import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterState } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { FetchUser, UserState } from 'src/app/common/state/user/user.state';
import { select } from 'src/app/common/utilities/ngxs-utils';
import { EnvironmentService } from './common/services/environment/environment.service';
import { gtag } from './common/services/google-analytics/gtag';
import { StorageService } from './common/services/storage/storage.service';
import { TemplatePageTitleService } from './common/services/template-page-title/template-page-title.service';
import { User } from './common/state/user/user.model';
import { GLEnvironment } from './common/types/env';
import { DomElement } from './common/utilities/dom-element';
import { UserPusherService } from './private/shared/services/pusher/user.pusher.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'growelab-web';

  user$: Observable<User | null> = select(UserState.getUser);

  database: string;

  envService = EnvironmentService;

  constructor(
    private store: Store,
    private router: Router,
    private titleService: TemplatePageTitleService,
    private userPusherService: UserPusherService
  ) {}

  /* eslint-disable-next-line class-methods-use-this */
  addScriptsByEnvironment() {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (window as any).environment = this.envService.env() as string;
    /* eslint-disable-next-line default-case */
    switch (this.envService.env()) {
      /* eslint-disable-next-line no-fallthrough */
      case GLEnvironment.QA:
      case GLEnvironment.STAGING:
      case GLEnvironment.PRODUCTION:
        /* eslint-disable-next-line no-case-declarations */
        const pendoLoader = DomElement.create('script');
        pendoLoader.innerHTML = `(function(apiKey){
    (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
    v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');})('d3644c12-2832-4a8c-7b1e-e960e3230e90');`;
        document.head.appendChild(pendoLoader);
        if (!StorageService.getAuthToken()) {
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          (window as any).pendo.initialize();
        } else {
          this.user$.subscribe((user) => {
            if (user) {
              /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
              (window as any).pendo.initialize({
                visitor: {
                  id: user.id,
                  district: user.district?.title,
                  district_id: user.district?.id,
                  school: user.school?.title,
                  school_id: user.school?.id,
                  user_type: user.coacheeType,
                  created_at: user.createdAt,
                  user_status: 'active',
                  coach_id: user.coach?.id,
                },
                account: {
                  id: user.district?.id,
                  name: user.district?.title,
                  creation_date: user.district?.createdAt,
                },
              });
            }
          });
        }
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        (window as any).googleAnalyticsID = this.envService.googleAnalyticsID();
        document.head.appendChild(
          DomElement.create(
            `script[async][src="https://www.googletagmanager.com/gtag/js?id=${this.envService.googleAnalyticsID()}"]`
          )
        );
        document.head.appendChild(
          DomElement.create(
            'script[async][src="/assets/js/google-tag-manager.js"]'
          )
        );
        break;
    }
  }

  ngOnInit(): void {
    if (StorageService.getAuthToken() && StorageService.getItem('user')?.id) {
      this.store.dispatch(FetchUser);
      this.userPusherService.addChannel(StorageService.getItem('user')?.id);
    }
    this.setDbValue();
    this.addScriptsByEnvironment();
    this.handleRouteEvents();
  }

  setDbValue() {
    // skipcq: JS-0383
    // eslint-disable-next-line default-case
    switch (this.envService.env()) {
      case GLEnvironment.LOCALQA:
        this.database = 'QA';
        break;
      case GLEnvironment.DEV:
        this.database = 'Local';
        break;
    }
  }

  handleRouteEvents() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const title = this.getTitle(this.router.routerState);

        gtag('event', 'page_view', {
          page_title: title,
        });
      }
    });
  }

  getTitle(state: RouterState): string {
    const data = this.titleService.updateTitle(state.snapshot);
    return data.getTitle();
  }
}
